/**
 * The admin area's shell — header (shared PlatformHeader) over a two-column
 * body: a grouped left SideNav and the scrollable Main content. Admin is a
 * role-gated *area* of the platform (reached via the "Admin" link in the chat
 * sidebar), so the SideNav leads with a "← Back to chat" link.
 *
 * The app supplies the nav sections; super-admin-only items are filtered for
 * tenant admins (empty sections dropped). Pages render `<AppShell>{...}</AppShell>`
 * and inherit Main's padding.
 */
import { Fragment, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import {
  AppShell as DSAppShell,
  SideNav,
  sideNavItemClassName,
} from '@servicecompressionllc/sc-design-system';
import {
  BookOpen,
  Library,
  Activity,
  ArrowRightFromLine,
  MessageSquareWarning,
  ArrowLeft,
  BarChart3,
  Bot,
  Boxes,
  Cable,
  Users,
  UsersRound,
  KeyRound,
  Route,
  Shield,
  Sparkles,
  ShieldCheck,
  Package,
  type LucideIcon,
  FileText,
} from '@servicecompressionllc/sc-design-system/icons';
import { PlatformHeader } from './PlatformHeader.js';
import { useMe } from '../hooks/useMe.js';
import { isSuperAdmin } from '../lib/roles.js';
import type { MeResponse } from '../lib/types.js';
import { PERMS, canOpenReports, hasAnyPermission, type Permission } from '../lib/permissions.js';

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  /** Platform-global item (scope catalog, modules) — SUPER_ADMIN role, not a
   *  tenant permission, because it administers the platform, not a tenant. */
  superAdminOnly?: boolean;
  /** Shown to holders of ANY listed permission — the one axis nav filters on
   *  (roles → permissions gate nav items like scopes gate agent tools).
   *  Matches the page's RequirePermission gate in App.tsx. */
  anyOfPermissions?: readonly Permission[];
  /** A user feature opened by scopes rather than an admin permission (Reports). */
  shownTo?: (user: MeResponse['user']) => boolean;
  /** Leading icon — gives each grouped row a visual anchor. */
  icon?: LucideIcon;
}

interface NavSection {
  /** Optional heading rendered above the section's items. */
  title?: string;
  items: NavItem[];
}

// Grouped by DOMAIN, not by "everything else": observability on top, then
// where data flows (Connections), the content the assistant serves (Knowledge
// Base), what acts on events (Automation), how the assistant behaves
// (Assistant), who may do what (Access), and the platform-global catalog.
// Every item names the permission(s) of its page's RequirePermission gate;
// empty sections drop out, so each caller sees exactly their surfaces.
const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      { to: '/usage', label: 'Usage', icon: BarChart3, anyOfPermissions: [PERMS.usage.view] },
      { to: '/reports', label: 'Reports', icon: FileText, shownTo: canOpenReports },
      { to: '/events', label: 'Events', icon: Activity, anyOfPermissions: [PERMS.events.view] },
      // knowledge.view admits the KB-pinned slice (the API pins the rows).
      {
        to: '/feedback',
        label: 'Feedback',
        icon: MessageSquareWarning,
        anyOfPermissions: [PERMS.feedback.view, PERMS.knowledge.view],
      },
    ],
  },
  {
    title: 'Connections',
    items: [
      {
        to: '/connections',
        label: 'Connections',
        icon: Cable,
        anyOfPermissions: [PERMS.integrations.manage],
      },
      {
        to: '/capabilities',
        label: 'Capabilities',
        icon: Boxes,
        anyOfPermissions: [PERMS.integrations.manage],
      },
    ],
  },
  {
    // Gated on the permission (KNOWLEDGE_ADMIN role + admins implicitly, and
    // only while the tenant has the module) — NOT on KnowledgeBase.Read; that
    // scope is retrieval, held by every mechanic's group.
    title: 'Knowledge Base',
    items: [
      {
        to: '/knowledge/categories',
        label: 'Categories',
        icon: Library,
        anyOfPermissions: [PERMS.knowledge.view],
      },
      {
        to: '/knowledge/rules',
        label: 'General Knowledge',
        icon: BookOpen,
        anyOfPermissions: [PERMS.knowledge.view],
      },
    ],
  },
  {
    // An event fires → a workflow acts on it, a webhook pushes it to
    // machines, a destination delivers it to humans.
    title: 'Automation',
    items: [
      {
        to: '/settings/workflows',
        label: 'Workflows',
        icon: Route,
        anyOfPermissions: [PERMS.workflows.manage],
      },
      {
        to: '/settings/webhooks',
        label: 'Webhooks',
        icon: ArrowRightFromLine,
        anyOfPermissions: [PERMS.webhooks.manage],
      },
    ],
  },
  {
    title: 'Assistant',
    items: [
      {
        to: '/settings/assistant',
        label: 'Assistant',
        icon: Bot,
        anyOfPermissions: [PERMS.assistant.manage],
      },
      {
        to: '/settings/bots',
        label: 'Bots',
        icon: Sparkles,
        anyOfPermissions: [PERMS.bots.manage],
      },
      {
        to: '/settings/guardrails',
        label: 'Safety & Guardrails',
        icon: Shield,
        anyOfPermissions: [PERMS.assistant.manage],
      },
    ],
  },
  {
    title: 'Access',
    items: [
      { to: '/users', label: 'Users', icon: Users, anyOfPermissions: [PERMS.users.manage] },
      {
        to: '/settings/groups',
        label: 'Groups',
        icon: UsersRound,
        anyOfPermissions: [PERMS.users.manage],
      },
      {
        to: '/settings/api-keys',
        label: 'API Keys',
        icon: KeyRound,
        anyOfPermissions: [PERMS.apiKeys.manage],
      },
    ],
  },
  {
    // Platform-global catalog, not tenant-scoped — SUPER_ADMIN only. The whole
    // section is filtered out for tenant admins (empty sections are dropped).
    title: 'Platform',
    items: [
      { to: '/settings/scopes', label: 'Scopes', superAdminOnly: true, icon: ShieldCheck },
      {
        to: '/settings/service-packages',
        label: 'Service packages',
        superAdminOnly: true,
        icon: Package,
      },
    ],
  },
];

interface AppShellProps {
  children: ReactNode;
  /** Fill the viewport instead of growing the page — for screens that scroll
      an inner region (e.g. table rows) and pin controls below it. */
  fill?: boolean;
}

export const AppShell = ({ children, fill = false }: AppShellProps) => {
  const me = useMe();
  const [navOpen, setNavOpen] = useState(false);

  const superAdmin = me.data ? isSuperAdmin(me.data.user.roles) : false;
  const user = me.data?.user;
  // One nav, one rule: an item shows iff the caller holds one of its
  // permissions (superAdminOnly = the platform-global role check).
  const visibleSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) =>
      item.superAdminOnly
        ? superAdmin
        : user !== undefined &&
          (item.shownTo?.(user) ?? hasAnyPermission(user, item.anyOfPermissions ?? [])),
    ),
  })).filter((section) => section.items.length > 0);

  return (
    <DSAppShell>
      <PlatformHeader onMenuClick={() => setNavOpen(true)} />
      <div className="flex min-h-0 flex-1">
        <SideNav open={navOpen} onOpenChange={setNavOpen}>
          <NavLink to="/" className={`${sideNavItemClassName(false)} mb-1`}>
            <ArrowLeft size={16} aria-hidden className="shrink-0" />
            <span>Back to chat</span>
          </NavLink>
          <SideNav.Divider />
          {visibleSections.map((section, i) => (
            <Fragment key={section.title ?? `section-${i}`}>
              {i > 0 && <SideNav.Divider />}
              <SideNav.Section title={section.title}>
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) => sideNavItemClassName(isActive)}
                    >
                      {Icon && <Icon size={16} aria-hidden className="shrink-0" />}
                      <span className="truncate">{item.label}</span>
                    </NavLink>
                  );
                })}
              </SideNav.Section>
            </Fragment>
          ))}
        </SideNav>
        {/* Admin content is data-dense (wide tables), so it gets a wider max
            width than the DS AppShell.Main default (max-w-7xl). Same box as the
            DS Main otherwise; not widened in the DS because that would also
            widen chat-style apps (Ask Ricky) that want a narrow column. */}
        {/* `min-w-0`: without it a flex item defaults to min-width:auto and
            refuses to shrink below its content, so a wide table (min-w-*) would
            blow the page out horizontally instead of scrolling inside its own
            overflow-x-auto container. */}
        <main className="min-w-0 flex-1 overflow-y-auto">
          <div
            className={
              fill
                ? 'mx-auto flex h-full max-w-[96rem] flex-col px-6 pb-6 pt-8'
                : 'mx-auto min-h-full max-w-[96rem] px-6 pb-12 pt-8'
            }
          >
            {children}
          </div>
        </main>
      </div>
    </DSAppShell>
  );
};
